import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

export type DbProduct = Tables<'products'> & {
  store?: Tables<'stores'> | null;
  category?: Tables<'categories'> | null;
};

export type DbStore = Tables<'stores'>;
export type DbCategory = Tables<'categories'>;

export const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return data as DbCategory[];
    },
  });
};

export const useStores = () => {
  return useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stores')
        .select('*')
        .eq('is_active', true)
        .order('rating', { ascending: false });
      if (error) throw error;
      return data as DbStore[];
    },
  });
};

export const useStore = (id: string | undefined) => {
  return useQuery({
    queryKey: ['store', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stores')
        .select('*')
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data as DbStore;
    },
    enabled: !!id,
  });
};

export const useProducts = (filters?: { badge?: string; storeId?: string; categoryId?: string; search?: string }) => {
  return useQuery({
    queryKey: ['products', filters],
    queryFn: async () => {
      let query = supabase
        .from('products')
        .select('*, store:stores(id, name, logo_url, is_verified), category:categories(id, name)')
        .eq('status', 'active');

      if (filters?.badge) query = query.eq('badge', filters.badge);
      if (filters?.storeId) query = query.eq('store_id', filters.storeId);
      if (filters?.categoryId) query = query.eq('category_id', filters.categoryId);
      if (filters?.search) query = query.ilike('name', `%${filters.search}%`);

      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;
      if (error) throw error;
      return data as DbProduct[];
    },
  });
};

export const useProduct = (id: string | undefined) => {
  return useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*, store:stores(id, name, logo_url, is_verified), category:categories(id, name)')
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data as DbProduct;
    },
    enabled: !!id,
  });
};

export const useSimilarProducts = (product: DbProduct | undefined) => {
  return useQuery({
    queryKey: ['similar-products', product?.id, product?.category_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*, store:stores(id, name, logo_url, is_verified)')
        .eq('status', 'active')
        .eq('category_id', product!.category_id!)
        .neq('id', product!.id)
        .limit(4);
      if (error) throw error;
      return data as DbProduct[];
    },
    enabled: !!product?.category_id,
  });
};

// Helper to convert DB product to the legacy Product type used by CartContext
import { Product } from '@/types';
export const toCartProduct = (p: DbProduct): Product => ({
  id: p.id,
  name: p.name,
  description: p.description || '',
  price: Number(p.price),
  oldPrice: p.old_price ? Number(p.old_price) : undefined,
  images: p.images || ['/placeholder.svg'],
  category: (p.category as any)?.name || '',
  storeId: p.store_id,
  storeName: (p.store as any)?.name || '',
  rating: Number(p.rating) || 0,
  reviewCount: p.review_count || 0,
  inStock: (p.stock ?? 0) > 0,
  colors: p.colors?.length ? p.colors : undefined,
  sizes: p.sizes?.length ? p.sizes : undefined,
  badge: p.badge as Product['badge'],
});
